import { useRef, useState } from "react";
import {
  ArtButton,
  ArtDialog,
  ArtTable,
  ArtTableHandle,
  confirmDialog,
  HeadCell,
  SSRTableBuilder,
  useApiService,
} from "@art/shared-ui";
import { faEdit, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import ArtRoleAddPage from "./add-dialog.component";
import { usePageWrapper, PermissionGate, usePermission } from "../../../hooks";
import { Box } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { tableParamNav } from "../../../utils/table-utils";
// import "../webhooks/styles/list.css"

function SessionRolesList() {
    const [queryParams, setQueryParams]: any = useSearchParams();

    const apiService = useApiService();
    const canWriteRoles = usePermission("APP_MANAGEMENT", "SESSION_ROLES", "Write");
    const canDeleteRoles = usePermission("APP_MANAGEMENT", "SESSION_ROLES", "Delete");
    const tableRef = useRef<ArtTableHandle>(null);
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [tableKey, setTableKey] = useState(0);
    const [tableSearchValue, setTableSearchValue] = useState(queryParams.get("search") || "");
    const [selectedData, setSelectedData]: [Data | null, any] = useState(null);

    interface Data {
      id: string;
      title: string;
      description: string;
    }

    const headCells: HeadCell<Data>[] = [
      {
        id: "title",
        numeric: false,
        disablePadding: false,
        label: "Title",
        getColData: (row) => row["title"],
      },
      {
        id: "description",
        numeric: false,
        disablePadding: false,
        label: "Description",
        getColData: (row) => row.description,
      },
    ];

    const tableBuilder = SSRTableBuilder<Data>({
      endpoint: "/roles/list",
      method: "get",
      apiService: apiService,

      onFetch: (params) => {
        let currentPage = params["page"] || "1";
        let searchText = params["search_text"] || ""; // search

        // return {
        //     "page": params["page"],
        //     "search_text": params["search_text"]
        // }
        //page and search added to params
        const newParams: any = {
          page: currentPage,
        };
        if (searchText) {
          newParams.search = searchText;
        }
        setQueryParams(newParams, tableParamNav(queryParams, "page", currentPage)); // Update this

        return {
          page: currentPage,
          search_text: searchText,
        };
      },
      onResponse: (data) => {
        return {
          data: data.data.roles || [],
          total: data.data.total_count || 0,
        };
      },
    });

    const openAddRoleDialog = (data?: Data) => {
      setSelectedData(data);
      setAddDialogOpen(true);
    };
    const closeAddRoleDialog = () => {
      setSelectedData(null);
      setAddDialogOpen(false);
    };

    const onSave = () => {
      closeAddRoleDialog();
      setTableSearchValue("");
      setTableKey((prev) => prev + 1);
    };

    return (
      <>
        <ArtDialog
          title={selectedData ? "Edit Role" : "Add Role"}
          open={addDialogOpen}
          onClose={closeAddRoleDialog}
          type="form"
          actionBuilder={(props) => {
            return (
              <Box sx={{ display: "flex", gap: "15px" }}>
                <ArtButton
                  buttonType="button"
                  dataQa="btn-save"
                  label={selectedData ? "Update" : "Add"}
                  onClick={() => props.submit?.()}
                />
                <ArtButton
                  buttonType="button"
                  dataQa="btn-reset"
                  label="Reset"
                  variant="outlined"
                  color="error"
                  onClick={() => props.reset?.()}
                />
              </Box>
            );
          }}
        >
          <ArtRoleAddPage data={selectedData} onSave={onSave} />
        </ArtDialog>
        <Box className="non-clickable-table">
          <ArtTable
            key={tableKey}
            ref={tableRef}
            headCells={headCells}
            builder={tableBuilder}
            search={{
              enabled: true,
              value: tableSearchValue,
            }}
            pagination={{
              initialPage: parseInt(queryParams.get("page") || "1") - 1,
            }}
            toolbarActions={[
              <PermissionGate application="APP_MANAGEMENT" feature="SESSION_ROLES" category="Write">
                <ArtButton
                  variant="contained"
                  label="Add"
                  dataQa="btn-add"
                  icon={faPlus}
                  onClick={() => {
                    openAddRoleDialog();
                  }}
                />
              </PermissionGate>,
            ]}
            rowActions={[
              ...(canWriteRoles ? [{
                icon: faEdit,
                label: "Edit",
                onClick: (row: Data) => {
                  openAddRoleDialog(row);
                },
              }] : []),

              ...(canDeleteRoles ? [{
                icon: faTrash,
                label: "Delete",
                onClick: (row: Data) => {
                  confirmDialog("confirm", "Do you want to delete?").then(() => {
                    apiService.deleteData(`/roles/${row?.id}`, {
                      onSuccess: () => {
                        onSave();
                      },
                      onError: (err: any) => {
                        console.log(err);
                      },
                    })();
                  });
                },
              },
              ] : [])
            ]}
          />
        </Box>
      </>
    );
}

const ArtRoles = usePageWrapper(SessionRolesList, {
  page_title: "Roles",
});

export { ArtRoles, SessionRolesList };
